import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Terminal, ArrowRight, Mail, Loader2, CheckCircle2, Sparkles } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const [params] = useSearchParams();
  const inviteEventId = params.get("invite_event");
  const inviteTitle = params.get("title");
  const inviteRole = params.get("role");

  const signIn = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const [email, setEmail] = useState("");
  const [magicState, setMagicState] = useState("idle"); // idle | sending | sent
  const requestMagicLink = async (e) => {
    e?.preventDefault?.();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setMagicState("sending");
    try {
      await api.post("/auth/magic/request", { email: trimmed });
      // Server always returns 200 (to prevent enumeration), so we can't
      // distinguish "sent" from "rate-limited" — surface a neutral success.
      setMagicState("sent");
    } catch (err) {
      console.error("magic-link request failed", err);
      toast.error("Could not send link. Try again.");
      setMagicState("idle");
    }
  };

  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const timeStr = clock.toLocaleTimeString(undefined, { hour12: false });
  const dateStr = clock.toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short", year: "numeric" }).toUpperCase();

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_460px] bg-canvas">
      {/* Left — control-center readout */}
      <div className="relative hidden md:flex flex-col justify-between border-r border-hair bg-surface overflow-hidden">
        {/* Grid backdrop */}
        <div className="absolute inset-0 control-grid opacity-70 pointer-events-none" />

        {/* Top: system window titlebar */}
        <div className="relative border-b border-hair px-8 py-4 bg-surface flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
              <img src="/atelis-icon-192.png" alt="AtelisOS" className="w-full h-full object-contain" />
            </div>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">AtelisOS · v1.2</span>
          </div>
          <div className="flex items-center gap-4 font-mono text-[10px] tracking-widest uppercase text-muted">
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full live-pulse" />
              System online
            </span>
            <span className="text-faint">|</span>
            <span className="tabular-nums text-app">{dateStr} · {timeStr}</span>
          </div>
        </div>

        {/* Middle: hero */}
        <div className="relative px-12 py-10 max-w-[720px]">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-6">SYSTEM // Event Operations OS</div>

          {/* Brand lockup */}
          <div className="mb-8 bg-[#0A0A0A] border border-[#0A0A0A] p-6 max-w-md">
            <img src="/atelis-logo-full.png" alt="AtelisOS — Event Planning Operating System" className="w-full h-auto block" />
          </div>

          <h1 className="font-display font-black text-[56px] leading-[0.95] tracking-[-0.03em] text-app max-w-[10ch]">
            Command every event you run.
          </h1>
          <p className="mt-6 text-app max-w-md leading-relaxed">
            Weddings, galas, tournaments, block parties — a technical control center for planners, vendors, and clients working on the same timeline.
          </p>

          {/* Module readout */}
          <div className="mt-10 grid grid-cols-2 gap-px bg-[#E4E4E7] border border-hair max-w-md">
            {[
              ["MOD.001", "Live command room"],
              ["MOD.002", "Multi-space floor plan"],
              ["MOD.003", "Budget · vendors · tasks"],
              ["MOD.004", "RSVP · invites · portals"],
            ].map(([code, label]) => (
              <div key={code} className="bg-surface p-3">
                <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted">{code}</div>
                <div className="text-sm text-app font-medium mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer: system log */}
        <div className="relative border-t border-hair px-8 py-3 font-mono text-[11px] text-muted tabular-nums bg-inset flex items-center gap-4 overflow-hidden">
          <span className="text-emerald-400">▸</span>
          <span>{timeStr}</span>
          <span className="text-faint">|</span>
          <span className="truncate">Awaiting operator authentication…</span>
        </div>
      </div>

      {/* Right — auth panel */}
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-3 flex items-center gap-1.5">
            <Terminal size={12} strokeWidth={2} /> AUTH.LOGIN
          </div>

          {inviteEventId && (
            <div
              className="mb-6 border border-hair bg-inset rounded-sm p-4 flex items-start gap-3"
              data-testid="login-invite-banner"
            >
              <div className="w-8 h-8 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center shrink-0">
                <Sparkles size={14} strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-1">
                  YOU&apos;VE BEEN INVITED
                </div>
                <div className="text-sm text-app leading-snug">
                  You&apos;ve been invited to collaborate on{" "}
                  <span className="font-medium">{inviteTitle || "an event"}</span>
                  {inviteRole && (<> as the <span className="font-medium">{inviteRole}</span></>)}
                  . Sign in or create your free AtelisOS account to continue.
                </div>
              </div>
            </div>
          )}

          <h2 className="font-display text-4xl font-black text-app leading-none tracking-tight mb-3">
            Sign in.
          </h2>
          <p className="text-sm text-muted mb-10 leading-relaxed">
            Continue with your Google workspace account to boot the control center.
          </p>

          <Button
            data-testid="login-google-btn"
            onClick={signIn}
            className="w-full rounded-sm bg-[#0A0A0A] hover:bg-black text-white h-12 font-medium tracking-wide justify-between px-4"
          >
            <span className="flex items-center gap-3">
              <GoogleIcon />
              <span>Continue with Google</span>
            </span>
            <ArrowRight size={15} strokeWidth={2} />
          </Button>

          {/* Divider */}
          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#E4E4E7]" />
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-faint">OR</span>
            <div className="h-px flex-1 bg-[#E4E4E7]" />
          </div>

          {/* Magic-link sign-in */}
          {magicState === "sent" ? (
            <div
              data-testid="magic-link-sent-panel"
              className="border border-hair bg-inset rounded-sm p-4 space-y-2"
            >
              <div className="flex items-center gap-2 text-[#059669]">
                <CheckCircle2 size={16} strokeWidth={2} />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase">LINK SENT</span>
              </div>
              <p className="text-sm text-app font-light leading-relaxed">
                If <span className="font-medium text-app">{email}</span> is a valid address, a one-time sign-in link is on its way. It expires in 15 minutes.
              </p>
              <button
                type="button"
                onClick={() => { setMagicState("idle"); setEmail(""); }}
                data-testid="magic-link-reset"
                className="text-xs text-muted hover:text-app underline underline-offset-2"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={requestMagicLink} className="space-y-2" data-testid="magic-link-form">
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted flex items-center gap-1.5">
                <Mail size={12} strokeWidth={2} /> Email me a sign-in link
              </label>
              <div className="flex gap-2">
                <Input
                  data-testid="magic-link-email-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-sm h-11 bg-surface border-hair"
                  disabled={magicState === "sending"}
                />
                <Button
                  type="submit"
                  data-testid="magic-link-submit-btn"
                  disabled={magicState === "sending"}
                  className="rounded-sm bg-surface text-app border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white h-11 px-4 font-medium tracking-wide disabled:opacity-60"
                >
                  {magicState === "sending" ? (
                    <Loader2 size={14} strokeWidth={2} className="animate-spin" />
                  ) : (
                    <ArrowRight size={15} strokeWidth={2} />
                  )}
                </Button>
              </div>
              <p className="text-[11px] text-muted font-light leading-relaxed">
                No Google account? No problem. We&rsquo;ll email you a one-time link — click it to sign in.
              </p>
            </form>
          )}

          <div className="mt-10 pt-6 border-t border-hair">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-3">Modules // included</div>
            <ul className="space-y-1.5 text-xs text-app">
              <li className="flex gap-2"><span className="text-muted font-mono">[01]</span> Dashboard per event · no template lock-in</li>
              <li className="flex gap-2"><span className="text-muted font-mono">[02]</span> Vendors see only their tasks · timeline · deliverables</li>
              <li className="flex gap-2"><span className="text-muted font-mono">[03]</span> Budget · guests · floor plan · RSVP · docs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 16.3 4 9.7 8.3 6.3 14.1z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.5l6.2 5.2C41.8 34.5 44 29.6 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
